/** Swimlane diagram data types */

/* ─── Step types ─── */
export type StepType = 'start' | 'end' | 'action' | 'decision' | 'switch' | 'subprocess' | 'data' | 'io' | 'error' | 'comment' | 'collapsed';
export type ConnectionType = 'normal' | 'error' | 'conditional';

/* ─── Lane (Zone) ─── */
export interface SwimlaneLane {
  id: string;           // "user", "app", "system", "partner"
  label: string;        // "Пользователь", "МП / Web"
  color?: string;       // background color for lane
  order?: number;       // vertical position (0 = top)
}

/* ─── Step (Block) ─── */
export interface SwimlaneStep {
  id: string;           // "sc001-s1"
  laneId: string;       // lane this step belongs to
  label: string;        // short text (max 25 chars)
  description?: string; // full description
  type?: StepType;
  order: number;        // sequence within scenario
}

/* ─── Connection (Edge) ─── */
export interface SwimlaneConnection {
  from: string;         // step id
  to: string;           // step id
  label?: string;       // "да", "нет", "ошибка"
  type?: ConnectionType;
}

/* ─── Scenario (a single business process) ─── */
export interface SwimlaneDiagram {
  id: string;
  title: string;
  description?: string;
  color?: string;       // unique scenario color
  lanes: SwimlaneLane[];
  steps: SwimlaneStep[];
  connections: SwimlaneConnection[];
  createdAt?: string;
  updatedAt?: string;
}

/* ─── Scenario visibility wrapper ─── */
export interface Scenario {
  id: string;
  title: string;
  description?: string;
  color: string;
  visible: boolean;
  diagram: SwimlaneDiagram;
}

/* ─── File wrapper ─── */
export interface DiagramFile {
  id: string;
  name: string;
  description?: string;
  diagram: SwimlaneDiagram;
}

/* ─── Collision detection ─── */
export interface CollisionError {
  type: 'duplicate_id' | 'cycle' | 'dangling_edge' | 'orphan_step' | 'invalid_lane';
  message: string;
  scenarioId?: string;
  stepId?: string;
  connectionId?: string;
}

/* ─── Step node data for React Flow ─── */
export interface StepNodeData extends Record<string, unknown> {
  label: string;
  description?: string;
  stepType: StepType;
  scenarioId: string;
  scenarioColor: string;
  laneId: string;
  laneColor: string;
}

/* ─── Lane node data for React Flow ─── */
export interface LaneNodeData extends Record<string, unknown> {
  label: string;
  color: string;
  laneId: string;
  order: number;
  height: number;
}

/* ─── Scenario color palette ─── */
export const SCENARIO_COLORS = [
  '#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#e91e63',
  '#00bcd4', '#ff5722', '#607d8b', '#795548', '#3f51b5',
  '#009688', '#cddc39',
];

/* ─── Lane color palette ─── */
export const LANE_COLORS: Record<string, string> = {
  user:             '#e3f2fd',
  app:              '#e8f5e9',
  system:           '#fff3e0',
  partner:          '#fce4ec',
  aml:              '#f3e5f5',
  operator:         '#e0f2f1',
  'partner-sumsub':   '#fce4ec',
  'partner-verestro': '#e8eaf6',
  'partner-teslapay': '#e1f5fe',
};

/* ─── Partner ─── */
export interface Partner {
  id: string;         // 'sumsub', 'verestro', 'teslapay'
  name: string;       // 'SumSub', 'Verestro', 'TeslaPay'
  color: string;      // lane background color
  description?: string;
}

export const DEFAULT_PARTNERS: Partner[] = [
  { id: 'sumsub',    name: 'SumSub',    color: '#fce4ec', description: 'KYC / верификация личности' },
  { id: 'verestro',  name: 'Verestro',  color: '#e8eaf6', description: 'Выпуск карт' },
  { id: 'teslapay',  name: 'TeslaPay',  color: '#e1f5fe', description: 'Платёжная инфраструктура' },
];
